import { EngineResult } from './constants'
import { sameDay } from './helpers'
import { decision } from './decision'
import { evaluateSchedule } from './evaluateSchedule'
import { isDateInSeason } from './isDateInSeason'

export function evaluateRecurringTemplate({
  template,
  existingTasks,
  operationalDate
}) {
  if (!template.is_active) {
    return decision({
      generate: false,
      code: EngineResult.INACTIVE,
      reason: 'Template inactive'
    })
  }

  if (template.is_paused) {
    return decision({
      generate: false,
      code: EngineResult.PAUSED,
      reason: 'Template paused'
    })
  }

  if (template.is_archived) {
    return decision({
      generate: false,
      code: EngineResult.ARCHIVED,
      reason: 'Template archived'
    })
  }

  // ==========================================================
  // EXISTING TASK / PERSISTENCE RULES
  // ==========================================================


  const existingTaskForDate = existingTasks.find(task =>
    task.recurring_task_id === template.id &&
    !task.is_hidden &&
    sameDay(task.due_date, operationalDate)
  )


  // The exact occurrence already has a Task.
  if (existingTaskForDate) {
    return decision({
      generate: false,
      code: EngineResult.ALREADY_GENERATED,
      reason: 'Task already exists for this occurrence date'
    })
  }

  // ----------------------------------------------------------
  // KEEP UNTIL DONE
  //
  // If an earlier generated task is still open, do not create
  // another task for the same recurring template.
  // ----------------------------------------------------------

  const existingOpenTask = existingTasks.find(task =>
    task.recurring_task_id === template.id &&
    !task.is_completed &&
    !task.is_hidden
  )

  if (
    template.persistence_type === 'keep_until_done' &&
    existingOpenTask
  ) {
    return decision({
      generate: false,
      code: EngineResult.ALREADY_GENERATED,
      reason: 'Existing recurring task is still open'
    })
  }


    // ==========================================================
  // DAY TYPE
  // ==========================================================
  //
  // day_type_levels:
  //
  //   1 = Workday
  //   2 = Non-Workday
  //   3 = Weekend
  //   4 = Everyday
  //
  // The occurrence date must match the template's day type
  // before the recurrence schedule can generate a task.
  // ==========================================================

  if (template.day_type_id != null) {

    const dayOfWeek =
      operationalDate.getDay()

    const isWeekend =
      dayOfWeek === 0 ||
      dayOfWeek === 6

    const isWorkday =
      !isWeekend

    const dayTypeMatches =
      Number(template.day_type_id) === 4 ||
      (
        Number(template.day_type_id) === 1 &&
        isWorkday
      ) ||
      (
        Number(template.day_type_id) === 2 &&
        !isWorkday
      ) ||
      (
        Number(template.day_type_id) === 3 &&
        isWeekend
      )

    if (!dayTypeMatches) {

      const dayTypeNames = {
        1: 'Workday',
        2: 'Non-Workday',
        3: 'Weekend',
        4: 'Everyday'
      }

      return decision({
        generate: false,
        code: EngineResult.NOT_SCHEDULED,
        reason:
          `Day type does not match: ${dayTypeNames[Number(template.day_type_id)] || 'Unknown'}`
      })
    }
  }

  
  /*
   * A Season is a calendar window, not a recurrence type.
   *
   * If a recurring template has a season, the occurrence
   * must fall inside that season before its Daily, Weekly,
   * Monthly, or Yearly schedule can generate it.
   */
  if (template.season_id) {
    const season = template.seasons

    if (!season) {
      return decision({
        generate: false,
        code: EngineResult.NOT_READY,
        reason: 'Season is assigned but season data is unavailable'
      })
    }

    if (!season.is_active) {
      return decision({
        generate: false,
        code: EngineResult.NOT_SCHEDULED,
        reason: 'Assigned season is inactive'
      })
    }

    const inSeason = isDateInSeason({
      date: operationalDate,
      season
    })

    if (!inSeason) {
      return decision({
        generate: false,
        code: EngineResult.NOT_SCHEDULED,
        reason: `Outside season window: ${season.name}`
      })
    }
  }

  console.log(
    'Evaluating recurrence:',
    template.name,
    template.recurrence_type_id
  )

  return evaluateSchedule({
  template,
  existingTasks,
  operationalDate
})
}